import * as React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { getFormattedDateTime, getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { useGetBillingRun } from 'src/features/billingRuns/api/useGetBillingRun';
import { billingRunsMessages } from 'src/features/billingRuns/utils/messages';
import { BILLING_RUN_STATUS_BADGE_OPTIONS, BILLING_RUN_TYPE_MAP } from 'src/features/billingRuns/utils/constants';
import BillingAttemptsTable from 'src/features/billingAttempts/components/tables/BillingAttemptsTable';

/*
 * The attempts list is embedded rather than being a child tab route: a tab row with exactly one tab
 * is not a tab row, and skipping it avoids the index-redirect and *-fallback routes that the tabbed
 * dashboards need.
 */
const BillingRunDashboard = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { billingRunId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(billingRunsMessages.billingRunDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: billingRun } = useGetBillingRun({ billingRunId });

    const dashboardBlockData = {
        section1: [
            {
                title: billingRunsMessages.businessDate,
                value: billingRun?.business_date ? getFormattedDateTime(billingRun.business_date).formattedDate : '-',
            },
            {
                // Null means the scheduled run, not missing data.
                title: billingRunsMessages.triggeredBy,
                value: billingRun?.triggered_by?.name ?? formatMessage(billingRunsMessages.scheduledRun),
            },
            {
                title: billingRunsMessages.totalCount,
                value: billingRun?.total_count ?? '-',
            },
            {
                title: billingRunsMessages.succeededCount,
                value: billingRun?.succeeded_count ?? '-',
            },
            {
                title: billingRunsMessages.failedCount,
                value: billingRun?.failed_count ?? '-',
            },
            {
                title: billingRunsMessages.skippedCount,
                value: billingRun?.skipped_count ?? '-',
            },
        ],
        section2: [
            {
                title: billingRunsMessages.startedAt,
                value: billingRun?.started_at ? getFormattedDateTimeForDetailPanel(billingRun.started_at) : '-',
            },
            {
                title: billingRunsMessages.finishedAt,
                value: billingRun?.finished_at ? getFormattedDateTimeForDetailPanel(billingRun.finished_at) : '-',
            },
        ],
        section3: [
            {
                title: billingRunsMessages.errorDetail,
                value: billingRun?.error_detail ? (
                    <span className="font-mono text-xs break-words whitespace-pre-wrap">{billingRun.error_detail}</span>
                ) : (
                    '-'
                ),
            },
        ],
    };

    const dashboardTitle = billingRun
        ? `${formatMessage(BILLING_RUN_TYPE_MAP[billingRun.type])} - ${getFormattedDateTime(billingRun.business_date).formattedDate}`
        : '';

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate('/billing-runs')}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(billingRunsMessages.backToAllBillingRuns)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{dashboardTitle}</SectionBlockTitle>
                        {billingRun && (
                            <Badge theme={BILLING_RUN_STATUS_BADGE_OPTIONS[billingRun.status].theme || 'gray'}>
                                <FontAwesomeIcon
                                    icon={BILLING_RUN_STATUS_BADGE_OPTIONS[billingRun.status].icon as IconProp}
                                />
                                {formatMessage(BILLING_RUN_STATUS_BADGE_OPTIONS[billingRun.status].label)}
                            </Badge>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={2} />
                </SectionBlock>
                <BillingAttemptsTable billingRunId={billingRunId} />
            </div>
        </>
    );
};

export default BillingRunDashboard;
